import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, ButtonBase, Grid, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton,
  Alert, CircularProgress, Divider, Tooltip, Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useNavigate } from 'react-router-dom';
import { branchService, SalonBrand, BranchSalon } from '../../services/inventoryService';
import { WorkingHoursEditor } from '../../components/WorkingHoursEditor';
import { PageHeader, SectionCard, EmptyState, LoadingState, StatusPill, useConfirm } from '../../components/ui';
import { tokens } from '../../theme';
import { countLabel } from '../../utils/format';

type BrandField = 'name' | 'description';
type BranchField = 'name' | 'address' | 'city' | 'postalCode' | 'phone' | 'email';

// Etykiety pol z API — w komunikatach nie pokazujemy technicznych nazw.
const API_FIELD_LABELS: Record<string, string> = {
  name: 'Nazwa',
  description: 'Opis',
  logoUrl: 'Logo',
  address: 'Adres',
  city: 'Miasto',
  postalCode: 'Kod pocztowy',
  phone: 'Telefon',
  email: 'E-mail',
};
const BRAND_FIELDS: string[] = ['name', 'description'];
const BRANCH_FIELDS: string[] = ['name', 'address', 'city', 'postalCode', 'phone', 'email'];

export default function BrandsPage() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [brands, setBrands] = useState<SalonBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState<Array<{ field?: string; message: string }>>([]);

  const [brandDialog, setBrandDialog] = useState(false);
  const [editingBrand, setEditingBrand] = useState<SalonBrand | null>(null);
  const [brandForm, setBrandForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);

  const [branchDialog, setBranchDialog] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState('');
  const [branchForm, setBranchForm] = useState({ name: '', address: '', city: '', postalCode: '', phone: '', email: '' });
  const [scheduleSalon, setScheduleSalon] = useState<{ id: string; name: string } | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      setErrorDetails([]);
      const data = await branchService.getBrands();
      setBrands(data);
    } catch {
      setError('Nie udało się wczytać marek. Odśwież stronę.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const clearErrors = () => {
    setError('');
    setErrorDetails([]);
  };

  const openCreateBrand = () => {
    setEditingBrand(null);
    setBrandForm({ name: '', description: '' });
    clearErrors();
    setBrandDialog(true);
  };

  const openEditBrand = (brand: SalonBrand) => {
    setEditingBrand(brand);
    setBrandForm({ name: brand.name, description: brand.description || '' });
    clearErrors();
    setBrandDialog(true);
  };

  const saveBrand = async () => {
    setSaving(true);
    try {
      if (editingBrand) {
        await branchService.updateBrand(editingBrand.id, brandForm);
      } else {
        await branchService.createBrand(brandForm);
      }
      setBrandDialog(false);
      clearErrors();
      load();
    } catch (e: any) {
      const details = e?.response?.data?.details || [];
      setErrorDetails(details);
      setError(details.length
        ? 'Nie udało się zapisać marki — sprawdź dane w formularzu.'
        : e?.response?.data?.error || 'Nie udało się zapisać marki. Spróbuj ponownie.');
    } finally {
      setSaving(false);
    }
  };

  const deleteBrand = async (id: string) => {
    const brand = brands.find(b => b.id === id);
    const branches = brand?._count?.salons ?? brand?.salons?.length ?? 0;
    const ok = await confirm({
      title: 'Usunąć markę?',
      details: brand ? (
        <>
          <strong>{brand.name}</strong>
          <Box component="span" sx={{ display: 'block', color: tokens.muted, fontSize: '0.8125rem' }}>
            {countLabel(branches, 'oddział', 'oddziały', 'oddziałów')} · {countLabel(brand._count?.products ?? 0, 'produkt', 'produkty', 'produktów')}
          </Box>
        </>
      ) : undefined,
      description: branches > 0
        ? 'Marki z oddziałami nie można usunąć — najpierw odłącz od niej wszystkie oddziały.'
        : 'Tej operacji nie można cofnąć.',
      confirmLabel: 'Usuń markę',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await branchService.deleteBrand(id);
      load();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Nie udało się usunąć marki. Spróbuj ponownie.');
    }
  };

  const openAddBranch = (brandId: string) => {
    setSelectedBrandId(brandId);
    setBranchForm({ name: '', address: '', city: '', postalCode: '', phone: '', email: '' });
    clearErrors();
    setBranchDialog(true);
  };

  const saveBranch = async () => {
    setSaving(true);
    try {
      const payload = {
        name: branchForm.name,
        address: branchForm.address,
        city: branchForm.city,
        postalCode: branchForm.postalCode,
        ...(branchForm.phone.trim() ? { phone: branchForm.phone.trim() } : {}),
        ...(branchForm.email.trim() ? { email: branchForm.email.trim() } : {}),
      };

      await branchService.createBranch(selectedBrandId, payload);
      setBranchDialog(false);
      clearErrors();
      load();
    } catch (e: any) {
      const details = e?.response?.data?.details || [];
      setErrorDetails(details);
      setError(details.length
        ? 'Nie udało się dodać oddziału — sprawdź dane w formularzu.'
        : e?.response?.data?.error || 'Nie udało się dodać oddziału. Spróbuj ponownie.');
    } finally {
      setSaving(false);
    }
  };

  const unlinkSalon = async (salonId: string) => {
    const salon = brands.flatMap(b => b.salons || []).find(s => s.id === salonId);
    const brand = brands.find(b => (b.salons || []).some(s => s.id === salonId));
    const ok = await confirm({
      title: 'Odłączyć oddział od marki?',
      details: salon ? (
        <>
          <strong>{salon.name}</strong>
          <Box component="span" sx={{ display: 'block', color: tokens.muted, fontSize: '0.8125rem' }}>
            {[salon.address, salon.city].filter(Boolean).join(', ')}{brand ? ` · marka ${brand.name}` : ''}
          </Box>
        </>
      ) : undefined,
      description: 'Salon nie zostanie usunięty — przestanie być oddziałem tej marki.',
      confirmLabel: 'Odłącz oddział',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await branchService.unlinkSalon(salonId);
      load();
    } catch {
      setError('Nie udało się odłączyć oddziału. Spróbuj ponownie.');
    }
  };

  const dialogFields = brandDialog ? BRAND_FIELDS : branchDialog ? BRANCH_FIELDS : [];
  const fieldError = (field: BrandField | BranchField) =>
    dialogFields.includes(field) ? errorDetails.find(d => d.field === field)?.message : undefined;
  const clearFieldError = (field: string) => {
    if (errorDetails.some(d => d.field === field)) setErrorDetails(list => list.filter(d => d.field !== field));
  };

  const renderError = () => {
    if (!error) return null;
    // Komunikaty przypiete do pol w otwartym oknie nie powtarzaja sie w belce.
    const rest = errorDetails.filter(d => !d.field || !dialogFields.includes(d.field));
    return (
      <Alert severity="error" onClose={clearErrors} sx={{ mb: 2 }}>
        <Stack spacing={0.5}>
          <Box>{error}</Box>
          {rest.length > 0 && (
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {rest.map((item, index) => (
                <li key={`${item.field || 'field'}-${index}`}>
                  {item.field && API_FIELD_LABELS[item.field] ? `${API_FIELD_LABELS[item.field]}: ` : ''}{item.message}
                </li>
              ))}
            </Box>
          )}
        </Stack>
      </Alert>
    );
  };

  const branchCount = brands.reduce((sum, b) => sum + (b.salons?.length ?? b._count?.salons ?? 0), 0);
  const selectedBrand = brands.find(b => b.id === selectedBrandId);

  const renderBranch = (salon: BranchSalon) => (
    <Paper variant="outlined" sx={{ height: '100%', bgcolor: tokens.surface, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2, pt: 1.75, pb: 1.25, flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Typography sx={{ fontWeight: 700, minWidth: 0, wordBreak: 'break-word' }}>{salon.name}</Typography>
          <Tooltip title="Odłącz od marki">
            <IconButton
              size="small"
              aria-label={`Odłącz oddział ${salon.name} od marki`}
              onClick={() => unlinkSalon(salon.id)}
              sx={{ mt: -0.5, mr: -0.75, '&:hover': { color: 'error.main' } }}
            >
              <LinkOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack direction="row" alignItems="flex-start" gap={0.5} sx={{ mt: 0.25, color: tokens.muted }}>
          <LocationOnOutlinedIcon sx={{ fontSize: 16, mt: '2px' }} />
          <Typography variant="caption" component="div">
            {[salon.address, [salon.postalCode, salon.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
          </Typography>
        </Stack>
        <Typography variant="caption" component="div" sx={{ mt: 0.75, color: tokens.inkSoft }}>
          {countLabel(salon._count?.staff ?? 0, 'pracownik', 'pracowników', 'pracowników')} · {countLabel(salon._count?.services ?? 0, 'usługa', 'usługi', 'usług')}
        </Typography>
      </Box>
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={0.25}
        sx={{ px: 1, py: 0.75, borderTop: `1px solid ${tokens.line}` }}
      >
        <Button size="small" startIcon={<WarehouseOutlinedIcon fontSize="small" />} onClick={() => navigate(`/salon/${salon.id}/inventory`)}>
          Magazyn
        </Button>
        <Button size="small" startIcon={<Inventory2OutlinedIcon fontSize="small" />} onClick={() => navigate(`/salon/${salon.id}/products`)}>
          Produkty
        </Button>
        <Button size="small" startIcon={<FactCheckOutlinedIcon fontSize="small" />} onClick={() => navigate(`/salon/${salon.id}/audits`)}>
          Inwentaryzacja
        </Button>
        <Button size="small" startIcon={<AccessTimeIcon fontSize="small" />} onClick={() => setScheduleSalon({ id: salon.id, name: salon.name })}>
          Godziny otwarcia
        </Button>
      </Stack>
    </Paper>
  );

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Usługi i magazyn' }]}
        title="Marki i oddziały"
        description={brands.length
          ? `${countLabel(brands.length, 'marka', 'marki', 'marek')} · ${countLabel(branchCount, 'oddział', 'oddziały', 'oddziałów')}. Każdy oddział ma własny magazyn i godziny otwarcia.`
          : 'Połącz lokalizacje swojego biznesu w marki z oddziałami, wspólnymi produktami i przesunięciami towaru.'}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateBrand}>
            Dodaj markę
          </Button>
        }
      />

      {!brandDialog && !branchDialog && renderError()}

      {loading ? (
        <LoadingState variant="cards" count={3} />
      ) : brands.length === 0 ? (
        <SectionCard>
          <EmptyState
            icon={<StorefrontOutlinedIcon />}
            title="Nie masz jeszcze marek"
            description="Utwórz markę, aby połączyć swoje salony w jedną sieć oddziałów ze wspólnym katalogiem produktów."
            actions={<Button variant="outlined" startIcon={<AddIcon />} onClick={openCreateBrand}>Dodaj markę</Button>}
          />
        </SectionCard>
      ) : (
        <Stack spacing={2.5}>
          {brands.map((brand) => (
            <SectionCard key={brand.id}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'flex-start' }} gap={1.5}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography component="h2" variant="h4" sx={{ wordBreak: 'break-word' }}>{brand.name}</Typography>
                  {brand.description && (
                    <Typography sx={{ color: tokens.inkSoft, mt: 0.25 }}>{brand.description}</Typography>
                  )}
                  <Stack direction="row" gap={0.75} mt={1} flexWrap="wrap">
                    <StatusPill noDot tone="neutral" label={countLabel(brand._count?.salons ?? 0, 'oddział', 'oddziały', 'oddziałów')} />
                    <StatusPill noDot tone="neutral" label={countLabel(brand._count?.products ?? 0, 'produkt', 'produkty', 'produktów')} />
                  </Stack>
                </Box>
                <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap" sx={{ flexShrink: 0 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SwapHorizIcon />}
                    onClick={() => navigate(`/salon/brands/${brand.id}/transfers`)}
                    sx={{ mr: 0.5 }}
                  >
                    Przesunięcia magazynowe
                  </Button>
                  <Tooltip title="Edytuj markę">
                    <IconButton aria-label={`Edytuj markę ${brand.name}`} onClick={() => openEditBrand(brand)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Usuń markę">
                    <IconButton
                      aria-label={`Usuń markę ${brand.name}`}
                      onClick={() => deleteBrand(brand.id)}
                      sx={{ '&:hover': { color: 'error.main' } }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Typography component="h3" sx={{ fontSize: '0.875rem', fontWeight: 700, color: tokens.muted, mb: 1.25 }}>
                Oddziały
              </Typography>
              <Grid container spacing={2}>
                {(brand.salons || []).map((salon) => (
                  <Grid item xs={12} sm={6} lg={4} key={salon.id}>
                    {renderBranch(salon)}
                  </Grid>
                ))}
                <Grid item xs={12} sm={6} lg={4}>
                  <ButtonBase
                    onClick={() => openAddBranch(brand.id)}
                    sx={{
                      width: '100%',
                      height: '100%',
                      minHeight: 120,
                      borderRadius: '12px',
                      border: `1.5px dashed ${tokens.lineStrong}`,
                      color: tokens.muted,
                      flexDirection: 'column',
                      gap: 0.75,
                      transition: 'border-color .15s, color .15s, background-color .15s',
                      '&:hover, &.Mui-focusVisible': { borderColor: tokens.plum, color: tokens.plum, bgcolor: tokens.plumSofter },
                    }}
                  >
                    <AddIcon />
                    <Typography sx={{ fontWeight: 650, fontSize: '0.9375rem', color: 'inherit' }}>Dodaj oddział</Typography>
                  </ButtonBase>
                </Grid>
              </Grid>
            </SectionCard>
          ))}
        </Stack>
      )}

      {/* Marka */}
      <Dialog open={brandDialog} onClose={() => { setBrandDialog(false); clearErrors(); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingBrand ? 'Edytuj markę' : 'Dodaj markę'}</DialogTitle>
        <DialogContent>
          {brandDialog && renderError()}
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              fullWidth label="Nazwa marki" value={brandForm.name}
              onChange={e => { setBrandForm(f => ({ ...f, name: e.target.value })); clearFieldError('name'); }}
              error={!!fieldError('name')}
              helperText={fieldError('name') || 'Nazwa, pod którą działają Twoje salony.'}
            />
            <TextField
              fullWidth label="Opis (opcjonalnie)" value={brandForm.description}
              onChange={e => { setBrandForm(f => ({ ...f, description: e.target.value })); clearFieldError('description'); }}
              multiline rows={3}
              error={!!fieldError('description')} helperText={fieldError('description')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => { setBrandDialog(false); clearErrors(); }}>Anuluj</Button>
          <Button
            variant="contained" onClick={saveBrand} disabled={saving || !brandForm.name}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {editingBrand ? 'Zapisz zmiany' : 'Dodaj markę'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Oddzial */}
      <Dialog open={branchDialog} onClose={() => { setBranchDialog(false); clearErrors(); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          Dodaj oddział
          {selectedBrand && (
            <Typography component="div" sx={{ color: 'text.secondary', fontSize: '0.9375rem', fontWeight: 500, mt: 0.25 }}>
              Marka {selectedBrand.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {branchDialog && renderError()}
          <Typography sx={{ color: 'text.secondary', mb: 2 }}>
            Oddział otrzyma własny magazyn i domyślne godziny otwarcia, które zmienisz później.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Nazwa oddziału" value={branchForm.name}
                onChange={e => { setBranchForm(f => ({ ...f, name: e.target.value })); clearFieldError('name'); }}
                error={!!fieldError('name')} helperText={fieldError('name')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Adres" value={branchForm.address} placeholder="ulica i numer"
                onChange={e => { setBranchForm(f => ({ ...f, address: e.target.value })); clearFieldError('address'); }}
                error={!!fieldError('address')} helperText={fieldError('address')}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth label="Miasto" value={branchForm.city}
                onChange={e => { setBranchForm(f => ({ ...f, city: e.target.value })); clearFieldError('city'); }}
                error={!!fieldError('city')} helperText={fieldError('city')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Kod pocztowy" value={branchForm.postalCode}
                placeholder="00-000"
                onChange={e => { setBranchForm(f => ({ ...f, postalCode: e.target.value })); clearFieldError('postalCode'); }}
                inputProps={{ inputMode: 'numeric' }}
                error={!!fieldError('postalCode')} helperText={fieldError('postalCode')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Telefon (opcjonalnie)" value={branchForm.phone} type="tel"
                onChange={e => { setBranchForm(f => ({ ...f, phone: e.target.value })); clearFieldError('phone'); }}
                error={!!fieldError('phone')} helperText={fieldError('phone')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="E-mail (opcjonalnie)" value={branchForm.email} type="email"
                onChange={e => { setBranchForm(f => ({ ...f, email: e.target.value })); clearFieldError('email'); }}
                error={!!fieldError('email')} helperText={fieldError('email')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => { setBranchDialog(false); clearErrors(); }}>Anuluj</Button>
          <Button
            variant="contained" onClick={saveBranch}
            disabled={saving || !branchForm.name || !branchForm.address || !branchForm.city || !branchForm.postalCode}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Dodaj oddział
          </Button>
        </DialogActions>
      </Dialog>

      <WorkingHoursEditor
        salonId={scheduleSalon?.id || null}
        salonName={scheduleSalon?.name}
        open={!!scheduleSalon}
        onClose={() => setScheduleSalon(null)}
      />
    </Box>
  );
}
